import { useEffect } from 'react';
import { toast } from 'sonner';

import { downloadAccountDataBundle } from './accountDataSyncApi';
import { getAccountSyncKey } from './syncKeyStore';
import {
  countAccountDataMergeRows,
  countAccountBundleRows,
  hasMeaningfulLocalAccountData,
  mergeAccountDataBundle,
  restoreAccountDataBundle,
} from './localAccountData';
import { useAccountIdentity, useAccountToken } from './accountAuthToken';
import { PassphraseSession } from '@/lib/crypto';

const AUTO_RESTORE_MARKER = 'bunko.account.autoRestoreAttempted.v2';

function storageMarkerKey(token: string): string {
  let hash = 0;
  for (let i = 0; i < token.length; i++) {
    hash = (hash * 31 + token.charCodeAt(i)) >>> 0;
  }
  return `${AUTO_RESTORE_MARKER}.${hash.toString(36)}`;
}

export function AccountSyncBootstrap() {
  const getAccountToken = useAccountToken();
  const accountIdentity = useAccountIdentity();

  useEffect(() => {
    let cancelled = false;
    async function bootstrap() {
      const token = await getAccountToken();
      if (!token || !accountIdentity || cancelled) return;
      const syncKey = await getAccountSyncKey(token);
      if (cancelled) return;
      PassphraseSession.setKey(syncKey, new Uint8Array(new ArrayBuffer(0)), accountIdentity);
      const markerKey = storageMarkerKey(token);
      if (cancelled || localStorage.getItem(markerKey) === 'true') return;
      const hasLocalData = await hasMeaningfulLocalAccountData();
      const bundle = await downloadAccountDataBundle(token, syncKey);
      localStorage.setItem(markerKey, 'true');
      if (!bundle || cancelled) return;
      if (hasLocalData) {
        const result = await mergeAccountDataBundle(bundle);
        const mergedRows = countAccountDataMergeRows(result);
        if (!cancelled && mergedRows > 0) {
          toast.success(`Merged ${mergedRows} account records.`);
        }
        return;
      }
      await restoreAccountDataBundle(bundle);
      if (!cancelled) {
        toast.success(`Restored ${countAccountBundleRows(bundle)} account records.`);
      }
    }

    bootstrap().catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [accountIdentity, getAccountToken]);

  return null;
}
